/**
 * CSV・Excel・PDF の出力 14 種と取込 4 種の定義（02 5.3.20・5.6。02 1.3 の src/csv/columns.ts）。
 * 画面（列の選択・取込の対応づけ）・API（検証・列定義の応答）・worker（出力・取込の処理）で同じ定義を使う。
 * - 出力の列名は 5.6.2 の「列名」（日本語）。列の選択ができるのは R-101・102・106・107 だけ（columns で並べ替え・絞り込み）
 * - 取込の列は 5.6.3（出力の列のうち書き込める列：D-073）。field は取込の行の項目名（物理名。主担当・営業担当などは取込用の名前）
 * 人間レビュー必須（02 11.1 No.8：取込の列・必須）。
 */
import type { PermissionCode } from '../authz/permission-definitions';

export const EXPORT_TYPE_VALUES = [
  'customerList',
  'companyList',
  'soleProprietorList',
  'caseList',
  'transactionList',
  'contractList',
  'personnelList',
  'userList',
  'auditLogList',
  'monthlyChecklist',
  'formSubmissionList',
  'reportDetail',
  'salesReportExcel',
  'salesReportPdf',
] as const;
export type ExportType = (typeof EXPORT_TYPE_VALUES)[number];

export const IMPORT_TYPE_VALUES = ['customer', 'personnel', 'contract', 'monthlyChecklist'] as const;
export type ImportType = (typeof IMPORT_TYPE_VALUES)[number];

/** 出力に要る権限（02 5.3.20 の表） */
export const EXPORT_PERMISSIONS: Readonly<Record<ExportType, PermissionCode>> = {
  customerList: 'customers:export',
  companyList: 'companies:export',
  soleProprietorList: 'sole_proprietors:export',
  caseList: 'cases:export',
  transactionList: 'transactions:export',
  contractList: 'contracts:export',
  personnelList: 'personnel:export',
  userList: 'users:export',
  auditLogList: 'audit_logs:export',
  monthlyChecklist: 'monthly_checklists:export',
  formSubmissionList: 'form_submissions:export',
  reportDetail: 'reports:export',
  salesReportExcel: 'reports:export',
  salesReportPdf: 'reports:export',
};

/** 取込に要る権限（02 5.3.20 の表） */
export const IMPORT_PERMISSIONS: Readonly<Record<ImportType, PermissionCode>> = {
  customer: 'customers:import',
  personnel: 'personnel:import',
  contract: 'contracts:import',
  monthlyChecklist: 'monthly_checklists:import',
};

/** ファイル名の種別名（5.6.1 `<種別名>_YYYYMMDD_HHMMSS.<拡張子>`） */
export const EXPORT_TYPE_FILE_NAMES: Readonly<Record<ExportType, string>> = {
  customerList: '顧客一覧',
  companyList: '企業一覧',
  soleProprietorList: '個人事業主一覧',
  caseList: '案件一覧',
  transactionList: '取引一覧',
  contractList: '契約一覧',
  personnelList: '要員一覧',
  userList: 'ユーザー一覧',
  auditLogList: '監査ログ',
  monthlyChecklist: '月次チェックリスト',
  formSubmissionList: 'フォーム回答',
  reportDetail: 'レポート詳細',
  salesReportExcel: '営業レポート',
  salesReportPdf: '営業レポート',
};

export const EXPORT_TYPE_EXTENSIONS: Readonly<Record<ExportType, 'csv' | 'xlsx' | 'pdf'>> = {
  customerList: 'csv',
  companyList: 'csv',
  soleProprietorList: 'csv',
  caseList: 'csv',
  transactionList: 'csv',
  contractList: 'csv',
  personnelList: 'csv',
  userList: 'csv',
  auditLogList: 'csv',
  monthlyChecklist: 'csv',
  formSubmissionList: 'csv',
  reportDetail: 'csv',
  salesReportExcel: 'xlsx',
  salesReportPdf: 'pdf',
};

const BANK_COLUMNS = [
  '銀行名',
  '支店名',
  '銀行コード',
  '支店コード',
  '口座種別',
  '口座番号',
  '口座名義',
  '口座名義カナ',
] as const;

/** 列の決まった出力（5.6.2。R-111 はフォームの設問、R-112〜114 はまとまり・シートのため除く） */
export const EXPORT_COLUMNS = {
  customerList: [
    '区分',
    'コード',
    '名前',
    '名前カナ',
    '郵便番号',
    '住所',
    'メールアドレス',
    '電話番号',
    'Webサイト',
    '代表者名',
    '担当者姓',
    '担当者名',
    '役職',
    '部署1',
    '部署2',
    '営業担当',
    '取引形態',
    '法人番号',
    'インボイス番号',
    '銀行名',
    '支店名',
    '銀行コード',
    '支店コード',
    '口座種別',
    '口座番号',
    '口座名義',
    '口座名義カナ',
    '取引先区分',
    'タグ',
    '登録日',
    '更新日',
  ],
  companyList: [
    '顧客コード',
    '企業名',
    '企業名カナ',
    'Webサイト',
    '代表者名',
    '郵便番号',
    '住所',
    '担当者姓',
    '担当者名',
    '役職',
    '部署1',
    '部署2',
    'メール',
    '電話番号',
    '営業担当',
    'インボイス番号',
    '法人番号',
    '取引形態',
    '取引先区分',
    ...BANK_COLUMNS,
    '登録日',
    '更新日',
  ],
  soleProprietorList: [
    '顧客コード',
    '事業主名',
    '事業主名カナ',
    '郵便番号',
    '住所',
    '電話番号',
    'メール',
    'インボイス番号',
    '取引先区分',
    ...BANK_COLUMNS,
    '登録日',
    '更新日',
  ],
  caseList: [
    '案件コード',
    '案件名',
    '顧客コード（企業）',
    '企業名',
    '顧客コード（個人事業主）',
    'ステータス',
    '金額',
    '予定クローズ日',
    '確度',
    '担当者',
    '説明',
    '登録日',
    '更新日',
  ],
  transactionList: [
    '取引コード',
    '顧客コード',
    '企業名',
    '担当者コード',
    '担当者名',
    '部署',
    '金額',
    'ステータス',
    '説明',
    '取引日',
    '数量',
    '支払条件',
    '商品・サービス',
    '商品の備考',
    '支払状況',
    '営業担当',
    '請求日',
    '支払予定日',
    '実支払日',
    '登録日',
    '更新日',
  ],
  contractList: [
    '契約コード',
    '要員コード',
    '要員名',
    '契約種類',
    'ステータス',
    '販売会社コード',
    '販売会社名',
    '販売部署',
    '販売単価',
    '販売支払サイト',
    '販売締日',
    '販売支払日',
    '仕入会社コード',
    '仕入会社名',
    '仕入単価',
    '仕入支払サイト',
    '仕入締日',
    '仕入支払日',
    '契約開始日',
    '契約終了日',
    '稼働時間下限',
    '稼働時間上限',
    '販売超過単価',
    '仕入超過単価',
    '販売控除単価',
    '仕入控除単価',
    '小数点処理',
    '利益',
    '利益率',
    '支援費',
    '支援会社コード',
    '備考',
    '登録日',
    '更新日',
  ],
  personnelList: [
    '要員コード',
    '要員区分',
    '氏名',
    '氏名カナ',
    'メール',
    '電話番号',
    '所属属性',
    'ステータス',
    '紹介元会社コード',
    '紹介元会社名',
    'スキル',
    '備考',
    '契約書送付先部署',
    '契約書送付先メール',
    '契約書送付先電話',
    '稼働表送付先メール',
    '稼働表送付先電話',
    'スキルシートURL',
    '登録日',
    '更新日',
  ],
  userList: ['メールアドレス', '名前', '会社名', '権限', '登録日', '更新日'],
  auditLogList: ['日時', '操作', '対象種別', '対象名', '操作者', 'IPアドレス'],
  monthlyChecklist: [
    '契約コード',
    '要員コード',
    '要員名',
    '年月',
    'タスク種別',
    'タスクコード',
    'タスク名',
    'カテゴリ',
    '完了',
    '完了者名',
    '完了日時',
  ],
} as const satisfies Partial<Record<ExportType, readonly string[]>>;

export type FixedColumnExportType = keyof typeof EXPORT_COLUMNS;

/** 列の選択ができる出力（R-101・102・106・107：02 5.3.20 columns） */
export const COLUMN_SELECTABLE_EXPORT_TYPES = ['customerList', 'companyList', 'contractList', 'personnelList'] as const;
export type ColumnSelectableExportType = (typeof COLUMN_SELECTABLE_EXPORT_TYPES)[number];

export function isColumnSelectableExportType(export_type: string): export_type is ColumnSelectableExportType {
  return (COLUMN_SELECTABLE_EXPORT_TYPES as readonly string[]).includes(export_type);
}

/** R-112 のまとまり（report_parameters.sections） */
export const REPORT_SECTION_VALUES = ['cases', 'activities', 'userSummary'] as const;
export type ReportSection = (typeof REPORT_SECTION_VALUES)[number];
export const REPORT_SECTION_LABELS: Readonly<Record<ReportSection, string>> = {
  cases: '案件データ',
  activities: '活動データ',
  userSummary: 'ユーザー別サマリー',
};

/** R-112 のまとまりごとの列（5.6.2） */
export const REPORT_DETAIL_COLUMNS: Readonly<Record<ReportSection, readonly string[]>> = {
  cases: [
    '案件コード',
    '案件名',
    '企業名',
    'ステータス',
    '金額',
    '確度(%)',
    '担当者',
    '作成日',
    '成約予定日',
    '商談期間(日)',
  ],
  activities: ['活動日', '活動タイプ', '方向', '担当者', '件名', '関連企業', '関連案件'],
  userSummary: ['担当者', '案件数', '成約数', '成約率(%)', '売上合計', '活動数', '平均商談期間(日)'],
};

// ───────── 取込（5.6.3） ─────────

export interface ImportColumnDefinition {
  /** CSV の見出し（出力の列名と同じ） */
  column_name: string;
  /** 取込の行の項目名 */
  field: string;
  is_required: boolean;
  /** 自動マッピング用の別名（03 3.12.3：正規化して完全一致） */
  aliases: readonly string[];
  description: string;
  example: string;
}

function column(
  column_name: string,
  field: string,
  options: { is_required?: boolean; aliases?: readonly string[]; description?: string; example?: string } = {},
): ImportColumnDefinition {
  return {
    column_name,
    field,
    is_required: options.is_required ?? false,
    aliases: [field, ...(options.aliases ?? [])],
    description: options.description ?? '',
    example: options.example ?? '',
  };
}

const IMPORT_BANK_COLUMNS: readonly ImportColumnDefinition[] = [
  column('銀行名', 'bank_name', { example: 'みずほ銀行' }),
  column('支店名', 'branch_name', { example: '東京営業部' }),
  column('銀行コード', 'bank_code', { description: '4 桁の数字', example: '0001' }),
  column('支店コード', 'branch_code', { description: '3 桁の数字', example: '001' }),
  column('口座種別', 'bank_account_type', { description: '普通・当座', example: '普通' }),
  column('口座番号', 'account_number', { description: '7 桁までの数字', example: '1234567' }),
  column('口座名義', 'account_holder_name', { example: 'アルファギケン（カ' }),
  column('口座名義カナ', 'account_holder_name_phonetic', { example: 'アルファギケン' }),
];

/** customer（company）：API-COMP-04／06 と同じ検証（主担当は担当者の列） */
const COMPANY_IMPORT_COLUMNS: readonly ImportColumnDefinition[] = [
  column('顧客コード', 'customer_code', {
    aliases: ['コード', '企業コード'],
    description: '空なら新規（自動採番）、あればその企業を更新',
    example: '',
  }),
  column('企業名', 'customer_name', {
    is_required: true,
    aliases: ['会社名', '名前'],
    example: '株式会社アルファ技研',
  }),
  column('企業名カナ', 'customer_name_phonetic', {
    is_required: true,
    aliases: ['会社名カナ', '名前カナ', 'フリガナ'],
    description: '全角カタカナ',
    example: 'アルファギケン',
  }),
  column('Webサイト', 'website_url', { aliases: ['URL', 'ホームページ'], example: 'https://alpha.example.com' }),
  column('代表者名', 'representative_name', { example: '山田 一郎' }),
  column('郵便番号', 'postal_code', { description: '123-4567 または 1234567', example: '100-0001' }),
  column('住所', 'address', { example: '東京都千代田区千代田1-1' }),
  column('担当者姓', 'contact_last_name', { description: '主担当として登録・更新', example: '井上' }),
  column('担当者名', 'contact_first_name', { description: '担当者姓があれば必須', example: '智子' }),
  column('役職', 'contact_job_title', { example: '部長' }),
  column('部署1', 'contact_department_1', { example: '営業部' }),
  column('部署2', 'contact_department_2', { example: '第一課' }),
  column('メール', 'contact_email', { aliases: ['メールアドレス', 'email'], example: 'inoue@alpha.example.com' }),
  column('電話番号', 'contact_phone_number', { aliases: ['TEL', '電話'], example: '03-1234-5678' }),
  column('営業担当', 'sales_user_email', { description: '利用者のメールアドレス（空欄可）', example: '' }),
  column('インボイス番号', 'invoice_number', { description: 'T＋13 桁の数字', example: 'T1234567890123' }),
  column('法人番号', 'corporate_number', { description: '13 桁の数字', example: '1234567890123' }),
  column('取引形態', 'trade_type', {
    description: '「; 」区切り（準委任・請負・派遣・その他）',
    example: '準委任; 派遣',
  }),
  column('取引先区分', 'trade_partner_type', { description: '「; 」区切り（販売先・仕入先）', example: '販売先' }),
  column('タグ', 'tag_name', { description: 'タグ名', example: '主要顧客' }),
  ...IMPORT_BANK_COLUMNS,
];

/** customer（soleProprietor）：API-SOLE-02／04 と同じ検証 */
const SOLE_PROPRIETOR_IMPORT_COLUMNS: readonly ImportColumnDefinition[] = [
  column('顧客コード', 'customer_code', {
    aliases: ['コード'],
    description: '空なら新規（自動採番）、あればその個人事業主を更新',
  }),
  column('事業主名', 'customer_name', { is_required: true, aliases: ['氏名', '名前'], example: '山田 太郎' }),
  column('事業主名カナ', 'customer_name_phonetic', {
    is_required: true,
    aliases: ['氏名カナ', '名前カナ', 'フリガナ'],
    example: 'ヤマダ タロウ',
  }),
  column('郵便番号', 'postal_code', { description: '123-4567 または 1234567', example: '150-0001' }),
  column('住所', 'address', { example: '東京都渋谷区神宮前1-1' }),
  column('電話番号', 'phone_number', { aliases: ['TEL', '電話'], example: '090-1234-5678' }),
  column('メール', 'email', { aliases: ['メールアドレス', 'email'], example: 'yamada@example.com' }),
  column('インボイス番号', 'invoice_number', { description: 'T＋13 桁の数字', example: 'T1234567890123' }),
  column('マイナンバー', 'my_number', { description: '12 桁の数字。空なら変えない', example: '' }),
  column('取引先区分', 'trade_partner_type', { description: '「; 」区切り（販売先・仕入先）', example: '仕入先' }),
  column('タグ', 'tag_name', { description: 'タグ名', example: '新規・小規模顧客' }),
  column('営業担当', 'sales_user_email', { description: '利用者のメールアドレス（空欄可）', example: '' }),
  ...IMPORT_BANK_COLUMNS,
];

const PERSONNEL_IMPORT_COLUMNS: readonly ImportColumnDefinition[] = [
  column('要員コード', 'personnel_code', { description: '空なら新規（自動採番）、あればその要員を更新' }),
  column('要員区分', 'personnel_type', { is_required: true, description: '自社社員・協力会社', example: '協力会社' }),
  column('氏名', 'personnel_name', { is_required: true, aliases: ['名前', '要員名'], example: '佐藤 花子' }),
  column('氏名カナ', 'personnel_name_phonetic', { aliases: ['フリガナ'], example: 'サトウ ハナコ' }),
  column('メール', 'email', { aliases: ['メールアドレス'], example: 'sato@example.com' }),
  column('電話番号', 'phone_number', { aliases: ['TEL', '電話'], example: '090-1111-2222' }),
  column('所属属性', 'affiliation_type', {
    is_required: true,
    description: '正社員・フリーランス・1社先の社員・2社先の社員・3社先の社員・1社先個人事業主',
    example: 'フリーランス',
  }),
  column('ステータス', 'personnel_status', { description: '契約前・稼働中・待機中・終了', example: '契約前' }),
  column('紹介元会社コード', 'referrer_customer_code', { example: '' }),
  column('スキル', 'skills', { description: '「; 」区切り', example: 'Java; AWS' }),
  column('備考', 'note', { example: '' }),
  column('契約書送付先部署', 'contract_document_department', { example: '総務部' }),
  column('契約書送付先メール', 'contract_document_email', { example: 'contract@example.com' }),
  column('稼働表送付先メール', 'timesheet_email', { example: 'timesheet@example.com' }),
  column('スキルシートURL', 'skill_sheet_url', { example: 'https://example.com/skill.pdf' }),
];

const CONTRACT_IMPORT_COLUMNS: readonly ImportColumnDefinition[] = [
  column('契約コード', 'contract_code', {
    description: '空なら新規（自動採番）、あればその契約を更新（単価は変えられない）',
  }),
  column('要員コード', 'personnel_code', { is_required: true, example: '1000001' }),
  column('契約種類', 'contract_type', { is_required: true, description: '準委任・請負・派遣', example: '準委任' }),
  column('ステータス', 'contract_status', {
    description: '契約前・稼働中・終了・中断（空なら契約前）',
    example: '稼働中',
  }),
  column('販売会社コード', 'sales_customer_code', { is_required: true, example: '10000001' }),
  column('販売単価', 'sales_unit_price', { is_required: true, example: '800000' }),
  column('販売支払サイト', 'payment_site', {
    is_required: true,
    aliases: ['支払サイト'],
    example: '月末締め翌月末払い',
  }),
  column('販売締日', 'sales_closing_day', { is_required: true, description: '1〜31（31 は月末）', example: '31' }),
  column('販売支払日', 'sales_payment_day', { is_required: true, description: '1〜31（31 は月末）', example: '31' }),
  column('仕入会社コード', 'supplier_customer_code', { example: '' }),
  column('仕入単価', 'supplier_unit_price', { example: '650000' }),
  column('仕入締日', 'supplier_closing_day', { example: '31' }),
  column('仕入支払日', 'supplier_payment_day', { example: '31' }),
  column('契約開始日', 'start_date', { is_required: true, description: 'YYYY/MM/DD', example: '2026/10/01' }),
  column('契約終了日', 'end_date', { description: 'YYYY/MM/DD', example: '2027/03/31' }),
  column('稼働時間下限', 'minimum_work_hours', { is_required: true, example: '140' }),
  column('稼働時間上限', 'maximum_work_hours', { is_required: true, example: '180' }),
  column('販売超過単価', 'sales_overtime_rate', { example: '5000' }),
  column('仕入超過単価', 'supplier_overtime_rate', { example: '4000' }),
  column('販売控除単価', 'sales_deduction_rate', { example: '5000' }),
  column('仕入控除単価', 'supplier_deduction_rate', { example: '4000' }),
  column('小数点処理', 'rounding_type', { description: '四捨五入・切り捨て・切り上げ', example: '四捨五入' }),
  column('支援費', 'sales_support_fee', { example: '0' }),
  column('営業担当', 'sales_user_email', { description: '利用者のメールアドレス（空欄可）', example: '' }),
  column('備考', 'note', { example: '長期契約案件' }),
];

const MONTHLY_CHECKLIST_IMPORT_COLUMNS: readonly ImportColumnDefinition[] = [
  column('契約コード', 'contract_code', { is_required: true, example: 'CT20260001' }),
  column('年月', 'year_month', { is_required: true, description: 'YYYY-MM', example: '2026-09' }),
  column('タスクコード', 'monthly_checklist_task_code', { aliases: ['タスクID'], description: '空ならタスク名で探す' }),
  column('タスク名', 'task_name', { description: 'タスクコードが空なら必須', example: '見積作成' }),
  // 274：旧の取込と同じく 仕入先／販売先 で同じ名前のタスク（請求書確認など）を区別する
  column('タスク種別', 'checklist_template_type', {
    description: '仕入先・販売先（同じ名前のタスクが両方にあるときは必須）',
    example: '販売先',
  }),
  column('完了', 'is_completed', { is_required: true, description: '○（完了）・×（未完了）', example: '○' }),
  column('完了日時', 'completed_at', { description: 'YYYY/MM/DD HH:mm:ss（空なら取込の時刻）', example: '' }),
];

export type ImportDefinitionKey = 'company' | 'soleProprietor' | 'personnel' | 'contract' | 'monthlyChecklist';

export const IMPORT_COLUMNS: Readonly<Record<ImportDefinitionKey, readonly ImportColumnDefinition[]>> = {
  company: COMPANY_IMPORT_COLUMNS,
  soleProprietor: SOLE_PROPRIETOR_IMPORT_COLUMNS,
  personnel: PERSONNEL_IMPORT_COLUMNS,
  contract: CONTRACT_IMPORT_COLUMNS,
  monthlyChecklist: MONTHLY_CHECKLIST_IMPORT_COLUMNS,
};

/** 取込の対象名（API-CSV-05 のファイル名 `<対象名>.csv`・`<対象名>_見本.csv`・エラー CSV） */
export const IMPORT_TARGET_NAMES: Readonly<Record<ImportDefinitionKey, string>> = {
  company: '顧客（企業）',
  soleProprietor: '顧客（個人事業主）',
  personnel: '要員',
  contract: '契約',
  monthlyChecklist: '月次チェックリスト',
};

/** import_type（と customer_type）から列定義の鍵を決める */
export function importDefinitionKeyOf(import_type: ImportType, customer_type?: string | null): ImportDefinitionKey {
  if (import_type === 'customer') {
    return customer_type === 'soleProprietor' ? 'soleProprietor' : 'company';
  }
  return import_type;
}

/** 1 ファイルの上限（仕様書 12 章・02 5.6.3） */
export const IMPORT_MAX_FILE_SIZE_BYTES = 10485760;
export const IMPORT_MAX_ROW_COUNT = 10000;
/** worker が 1 回のメッセージで処理する行数（要件書 3.2） */
export const IMPORT_CHUNK_ROW_COUNT = 1000;

/** 自動マッピングの正規化（03 FE-D-049：NFKC・前後の空白除去・英字の小文字化・全角/半角の空白除去） */
export function normalizeColumnHeader(header: string): string {
  return header
    .normalize('NFKC')
    .trim()
    .toLowerCase()
    .replace(/[\s\u3000]/g, '');
}

/**
 * CSV の見出しを列定義に自動で対応づける（03 3.12.3）。当たらない見出しは null。
 * 同じ取込先が 2 つの見出しに当たったら、先の見出しを優先する。
 */
export function autoMapColumns(
  headers: readonly string[],
  columns: ReadonlyArray<Pick<ImportColumnDefinition, 'column_name' | 'field' | 'aliases'>>,
): Record<string, string | null> {
  const by_name = new Map<string, string>();
  for (const definition of columns) {
    for (const name of [definition.column_name, ...definition.aliases]) {
      const key = normalizeColumnHeader(name);
      if (!by_name.has(key)) {
        by_name.set(key, definition.field);
      }
    }
  }
  const used = new Set<string>();
  const mapping: Record<string, string | null> = {};
  for (const header of headers) {
    const field = by_name.get(normalizeColumnHeader(header)) ?? null;
    if (field && !used.has(field)) {
      used.add(field);
      mapping[header] = field;
    } else {
      mapping[header] = null;
    }
  }
  return mapping;
}

/** 対応づけに無い必須の列（取込ボタンの上に出す・API-CSV-07 の IMPORT_005） */
export function missingRequiredColumns(
  mapping: Readonly<Record<string, string | null>>,
  columns: ReadonlyArray<Pick<ImportColumnDefinition, 'column_name' | 'field' | 'is_required'>>,
): string[] {
  const mapped = new Set(Object.values(mapping).filter((field): field is string => Boolean(field)));
  return columns
    .filter((definition) => definition.is_required && !mapped.has(definition.field))
    .map((d) => d.column_name);
}
